import React from 'react';

import {useTranslations} from 'src/components/TranslationsContext';
import {Project} from 'src/lib/api/project';
import {RegionalReportResponse} from 'src/lib/api/regionalReport';

import AggregatedSummaryRow from './AggregatedSummaryRow';
import {
  generateExecutiveSummary,
  getAggregatedMedianPct,
  getSiteMedianPct,
  precipToDateCaption,
  precipToDateMethodology,
  primaryTargetPeriod,
  upcomingBlocks,
  weeklyOutlookCaption,
  weeklyOutlookMethodology,
} from './commentary';
import ForecastEvolutionChart from './ForecastEvolutionChart';
import GefsOutlookGauges from './GefsOutlookGauges';
import OutlookGauge from './OutlookGauge';
import PrecipToDateChart from './PrecipToDateChart';
import RegionalMap from './RegionalMap';
import SiteDetailSection from './SiteDetailSection';
import SynopsisTable from './SynopsisTable';
import WeeklyOutlookChart from './WeeklyOutlookChart';

type Props = {
  project: Pick<Project, 'sites'>;
  report: RegionalReportResponse;
  dateRange: [Date, Date];
  snapshotDate?: string;
};

export default function RegionalReportDocument({project, report, dateRange, snapshotDate}: Props) {
  const {t} = useTranslations();
  const executiveSummary = generateExecutiveSummary(report);
  const medianPct = getAggregatedMedianPct(report);
  const sitesById = new Map(project.sites?.map((s) => [s.id, s]) ?? []);

  return (
    <div className="max-w-4xl mx-auto px-8 py-10 print:px-0 print:py-0 print:max-w-none print:[&_button]:hidden [&_h2]:break-after-avoid [&_h3]:break-after-avoid [&_h4]:break-after-avoid">
      {/* Report Header */}
      <header className="mb-8 border-b-2 border-gray-800 pb-4 flex items-start gap-4">
        <img
          src="/assets/hf-logo.svg"
          className="h-[30px] w-auto mt-1 shrink-0"
          alt="HydroForecast"
        />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Long-Range Streamflow Forecast</h1>
          <p className="text-lg text-gray-700 mt-1">{report.basinLabel}</p>
          <p className="text-sm text-gray-500 mt-2">
            Prepared {report.reportDate} &middot; Forecast water year: {report.forecastYear}{' '}
            &middot; {report.sitesIncluded} of {report.siteCount} sites included
          </p>
        </div>
      </header>

      {/* Table of Contents */}
      <TableOfContents report={report} />

      {/* Executive Summary — narrative, outlook gauge, anomaly gauges, map,
          and full per-site synopsis table all in one section. */}
      {executiveSummary.length > 0 && (
        <section id="executive-summary" className="mb-8">
          <h2 className="text-lg font-bold mb-2">Executive Summary</h2>
          <div className="flex gap-6 items-start">
            <div className="flex-1">
              <p className="text-sm text-gray-800 leading-relaxed">
                {executiveSummary.map((sentence, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && ' '}
                    {typeof sentence === 'string' ? (
                      sentence
                    ) : (
                      <>
                        {sentence.before}
                        <a
                          href={sentence.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {sentence.linkText}
                        </a>
                        {sentence.after}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </p>
              <AggregatedSummaryRow report={report} />
            </div>
            {medianPct != null && (
              <div className="shrink-0">
                <OutlookGauge
                  percentOfNormal={medianPct}
                  label={`${primaryTargetPeriod(report)?.label ?? ''} ${report.forecastYear}`}
                />
              </div>
            )}
          </div>
          <div className="mt-4">
            <GefsOutlookGauges gefs={report.gefsOutlook} cpc={report.cpcOutlook} />
          </div>

          <div className="mt-6 break-inside-avoid">
            <RegionalMap
              sites={report.sites.map((s) => ({
                ...s,
                medianPctNormal: getSiteMedianPct(report, s.siteId),
              }))}
              basinOutline={report.basinOutline}
            />
          </div>

          <div className="mt-6">
            <h3 className="text-base font-semibold mb-1">Forecast Synopsis</h3>
            <p className="text-xs text-gray-500 mb-2 break-after-avoid">
              Forecast volume as percent of normal (long-term average). A dash means the forecast
              does not cover the whole period. All Sites adds up the sites&apos; volumes; their
              basins overlap, so it is a regional summary, not a forecast of total basin volume.
            </p>
            <SynopsisTable report={report} />
          </div>
        </section>
      )}

      {hasWeatherSection(report) && (
        <section id="weather-outlook" className="mb-8">
          <h2 className="text-lg font-bold mb-1">Precipitation So Far and Weekly Outlook</h2>
          {report.precipToDate && (
            <div className="break-inside-avoid mb-6">
              <h3 className="text-base font-semibold mb-1">Precipitation since Oct 1</h3>
              <p className="text-xs text-gray-500 mb-2">
                {precipToDateCaption(report.precipToDate)}
              </p>
              <PrecipToDateChart data={report.precipToDate} />
            </div>
          )}
          {hasWeeklyOutlook(report) && (
            <div className="break-inside-avoid">
              <h3 className="text-base font-semibold mb-1">Weekly outlook</h3>
              <p className="text-xs text-gray-600 mb-2">
                {weeklyOutlookCaption(report.gefsOutlook, report.ecmwfOutlook ?? null)}
              </p>
              <WeeklyOutlookChart gefs={report.gefsOutlook} ecmwf={report.ecmwfOutlook ?? null} />
            </div>
          )}
        </section>
      )}

      {/* Forecast Evolution — volume boxplots over past 2 weeks */}
      {report.forecastEvolution &&
        (report.forecastEvolution.hf.length > 0 || report.forecastEvolution.nwrfc.length > 0) && (
          <section id="forecast-evolution" className="mb-8 break-inside-avoid">
            <h2 className="text-lg font-bold mb-3">Forecast Evolution Over the Past Two Weeks</h2>
            <ForecastEvolutionChart evolution={report.forecastEvolution} />
          </section>
        )}

      {/* Per-Site Details */}
      <section id="individual-sites" className="mb-8">
        <h2 className="text-lg font-bold mb-4 border-b border-gray-300 pb-1">
          Individual Site Forecasts
        </h2>
        {report.sites.map((site) => (
          <SiteDetailSection
            key={site.siteId}
            site={site}
            fullSite={sitesById.get(site.siteId)}
            siteSynopsis={report.synopsis[site.siteId] || {}}
            targetPeriods={report.targetPeriods}
            forecastYear={report.forecastYear}
            reportDate={report.reportDate}
            snapshotDate={snapshotDate}
            dateRange={dateRange}
            sweData={report.perSiteSWE?.[site.siteId]}
            allSites={report.sites}
            basinOutline={report.basinOutline}
          />
        ))}
      </section>

      {/* Methodology Footer */}
      <footer className="border-t border-gray-300 pt-4 mt-10 text-xs text-gray-500 leading-relaxed break-inside-avoid">
        <h2 className="text-sm font-semibold text-gray-700 mb-1">Methodology &amp; Notes</h2>
        <p>
          Streamflow forecasts are generated by HydroForecast, a machine-learning-based hydrologic
          forecasting system developed by Upstream Tech. Forecasts incorporate meteorological
          forcing data, satellite-derived snow and soil moisture observations, and historical
          streamflow records. NWRFC ESP Natural forecasts are shown where available for comparison.
          Exceedance probabilities represent the likelihood that actual flows will exceed the
          indicated value. The long-term average (LTA) is derived from historical gauge
          observations. &ldquo;Normal&rdquo; refers to the long-term average (LTA) streamflow for
          each day of the water year, derived from the historical gauge observations referenced
          above.
        </p>
        {report.ltaReferencePeriod && (
          <p className="mt-2">
            {t('regionalReportLtaYears', {year: String(report.ltaReferencePeriod.cutoffYear)})}
          </p>
        )}
        <p className="mt-2">{t('regionalReportSnowSources')}</p>
        {precipToDateMethodology(report.precipToDate) && (
          <p className="mt-2">{precipToDateMethodology(report.precipToDate)}</p>
        )}
        {weeklyOutlookMethodology(report.gefsOutlook, report.ecmwfOutlook ?? null) && (
          <p className="mt-2">
            {weeklyOutlookMethodology(report.gefsOutlook, report.ecmwfOutlook ?? null)}
          </p>
        )}
        <p className="mt-2">
          Snowpack percentages compare with the same date in past years; early in the season, small
          amounts of snow give large percentages that say little about spring runoff. Snow cover
          (NDSI) is averaged over each site&apos;s contributing area.
        </p>
        <p className="mt-2">
          This report is provided for informational purposes. Forecast accuracy varies by location
          and lead time. For questions or to provide feedback, contact Upstream Tech.
        </p>
      </footer>
    </div>
  );
}

function hasWeeklyOutlook(report: RegionalReportResponse): boolean {
  return (
    upcomingBlocks(report.gefsOutlook?.weekly).length > 0 ||
    upcomingBlocks(report.ecmwfOutlook?.weekly).length > 0
  );
}

function weatherSiteName(report: RegionalReportResponse): string | undefined {
  return (
    report.precipToDate?.siteName ??
    report.gefsOutlook?.siteName ??
    report.ecmwfOutlook?.siteName ??
    undefined
  );
}

function hasWeatherSection(report: RegionalReportResponse): boolean {
  return Boolean(report.precipToDate) || hasWeeklyOutlook(report);
}

function TableOfContents({report}: {report: RegionalReportResponse}) {
  const hasEvolution =
    report.forecastEvolution &&
    (report.forecastEvolution.hf.length > 0 || report.forecastEvolution.nwrfc.length > 0);

  return (
    <nav className="mb-8 text-sm">
      <h2 className="text-lg font-bold mb-2">Contents</h2>
      <ol className="list-decimal list-inside space-y-1 text-gray-700">
        <li>
          <a href="#executive-summary" className="hover:underline">
            Executive Summary
          </a>
        </li>
        {hasWeatherSection(report) && (
          <li>
            <a href="#weather-outlook" className="hover:underline">
              Precipitation So Far and Weekly Outlook
            </a>
            {weatherSiteName(report) && (
              <span className="text-gray-400 ml-1">— {weatherSiteName(report)}</span>
            )}
          </li>
        )}
        {hasEvolution && (
          <li>
            <a href="#forecast-evolution" className="hover:underline">
              Forecast Evolution Over the Past Two Weeks
            </a>
            {report.forecastEvolution.siteName && (
              <span className="text-gray-400 ml-1">— {report.forecastEvolution.siteName}</span>
            )}
          </li>
        )}
        <li>
          <a href="#individual-sites" className="hover:underline">
            Individual Site Forecasts
          </a>
          <ol className="list-[lower-alpha] list-inside ml-4 mt-1 space-y-0.5 text-gray-500">
            {report.sites.map((site) => (
              <li key={site.siteId}>
                <a href={`#site-${site.siteId}`} className="hover:underline">
                  {site.name}
                </a>
              </li>
            ))}
          </ol>
        </li>
      </ol>
    </nav>
  );
}
